import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Clock, Send, MessageCircle, ArrowRight, Building, Users, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      
      if (result.success) {
        alert(result.message);
        setFormData({
          name: '',
          email: '',
          phone: '',
          organization: '',
          subject: '',
          message: ''
        });
      } else {
        alert(result.message || 'There was an error submitting your form. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an error submitting your form. Please try again or contact us directly at mediquipaf@gmail.com');
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: '+234 906 305 9897',
      subText: 'Mon-Fri 8AM-6PM WAT',
      color: 'teal',
      action: 'tel:+2349063059897'
    },
    {
      icon: Mail,
      title: 'Email',
      details: 'mediquipaf@gmail.com',
      subText: 'We respond within 24 hours',
      color: 'blue',
      action: 'mailto:mediquipaf@gmail.com'
    }
  ];

  const reasons = [
    {
      icon: Users,
      title: 'Expert Team',
      description: 'Extensive healthcare expertise and medical experience'
    },
    {
      icon: Award,
      title: 'Proven Excellence',
      description: 'Track record of successful partnerships and quality delivery'
    },
    {
      icon: Building,
      title: 'Trusted Partner',
      description: 'Reliable distribution network across West Africa'
    }
  ];

  return (
    <div className="pt-20">
      {/* Page Header */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-teal-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Let's Work Together
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Contact us today so Mediquip can help your healthcare facility achieve its goals, 
              and we can, together, redefine medical consumables distribution in West Africa.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6">
                  Contact Information
                </h2>
                <p className="text-slate-600 mb-8">
                  Ready to transform your healthcare facility's supply chain? 
                  Reach out to us through any of these channels.
                </p>
              </div>

              <div className="space-y-6">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  const colorClasses = {
                    teal: 'bg-teal-100 text-teal-600 border-teal-200',
                    blue: 'bg-blue-100 text-blue-600 border-blue-200',
                    green: 'bg-green-100 text-green-600 border-green-200'
                  };

                  const ContactItem = info.action ? 'a' : 'div';
                  const itemProps = info.action ? { href: info.action } : {};

                  return (
                    <ContactItem
                      key={index}
                      {...itemProps}
                      className={`flex items-start space-x-4 p-4 rounded-xl border border-slate-100 hover:shadow-md transition-all duration-200 ${info.action ? 'hover:bg-slate-50 cursor-pointer' : ''}`}
                    >
                      <div className={`w-12 h-12 ${colorClasses[info.color]} rounded-lg flex items-center justify-center border-2 flex-shrink-0`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">{info.title}</h3>
                        <p className="text-slate-700 font-medium">{info.details}</p>
                        <p className="text-slate-500 text-sm">{info.subText}</p>
                      </div>
                    </ContactItem>
                  );
                })}
              </div>

              {/* Business Hours */}
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    <Clock className="w-5 h-5 text-slate-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900">Business Hours</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Monday - Friday</span>
                    <span className="text-slate-900 font-medium">8:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Saturday</span>
                    <span className="text-slate-900 font-medium">9:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Sunday</span>
                    <span className="text-slate-500">Closed</span>
                  </div>
                </div>
              </div>

              {/* Why Choose Us */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900">Why Partner With Us?</h3>
                {reasons.map((reason, index) => {
                  const Icon = reason.icon;
                  return (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-teal-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900 text-sm">{reason.title}</h4>
                        <p className="text-slate-600 text-xs">{reason.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 shadow-lg">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">
                  Send us a Message
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                        Full Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full"
                        placeholder="Your full name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                        Email Address *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                        Phone Number
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full"
                        placeholder="+234 xxx xxx xxxx"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="organization" className="block text-sm font-medium text-slate-700 mb-2">
                        Healthcare Facility
                      </label>
                      <Input
                        id="organization"
                        name="organization"
                        type="text"
                        value={formData.organization}
                        onChange={handleInputChange}
                        className="w-full"
                        placeholder="Hospital/Clinic name"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-2">
                      Subject *
                    </label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      required
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full"
                      placeholder="How can we help?"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full resize-none"
                      placeholder="Tell us about your healthcare facility's needs and how we can help..."
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center justify-center space-x-2"
                  >
                    <Send className="w-5 h-5" />
                    <span>Send Message</span>
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-8 lg:p-12 border border-slate-100 text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Prefer to Explore First?
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              Learn more about our services, leadership team, and vision before reaching out. 
              We're here when you're ready to transform your healthcare supply chain.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/services"
                className="inline-flex items-center px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-all duration-200"
              >
                Explore Our Services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center px-6 py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:border-teal-600 hover:text-teal-600 transition-all duration-200"
              >
                Learn About Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;